import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import PortfolioDealsEmptyState from 'components/PortfolioDealsEmptyState';
import PortfolioHeading from '../../../../components/PortfolioHeading';
import ListingPortfolioCard from 'components/ListingPortfolioCard';
import { PortfolioQueryParams } from '../../../../components/PortfolioFilterBar/models';
import useQueryParams from 'hooks/useQueryParams';
import { DealRoom } from 'models/DealRoom';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  PortfolioSellerTable,
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  TABLE_COLUMNS,
  CUSTOM_COL_SIZES,
  ENTERPRISE_TABLE_COLUMNS,
  BOSS_MODE_TABLE_COLUMNS,
  LCW_HEADING_TRANSLATIONS,
  REQUIRED_COLUMNS,
} from './components/SellerDealTableCells/data';
import { BODY_CONTENT_GENERATE } from './components/SellerDealTableCells';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { PortfolioSellerFilters } from '../../../../components/PortfolioSellerFilters';
import { useNavigate } from 'react-router-dom';
import { FIRST_PAGE, NEW_WINDOW_PROPS } from 'data/global';
import { ViewSelect } from '../../../../components/ViewSelect';
import { EnterpriseSellerEmptyState } from './components/EntepriseSellerEmptyState';
import useActiveBanner from './hooks/useActiveBanner';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { ManageListViewDrawer } from '../../../../components/ManageListViewDrawer';
import { HIDE_TABLE_COLUMNS } from 'models/User';
import useSearchParams from 'hooks/useSearchParams';
import ResolvePagination from 'components/ResolvePagination';
import { PF_GRID_VIEW_LIMIT, SELLING_DEFAULT_SORT } from '../../../data';
import PortfolioPlaceholderCards from '../../../Buyer/components/PortfolioPlaceholderCards';
import PortfolioSellerOptions from '../../../../components/PortfolioSellerOptions';
import {
  PORTFOLIO_FILTER,
  PortfolioFilter,
} from 'router/subrouters/Dash/subrouters/hooks/useBusinessesFilters';
import EditColumnsVisibilityButton from 'components/EditColumnsVisibilityButton';
import { Button } from 'ncoded-component-library';
import utils, { getIsListingOwner } from 'utils';
import { DropzoneFile } from 'components/Dropzone/utils';
import MultiSortModal from 'components/MultiSortModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import ArrowupIcon from 'icons/Arrowup.icon';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import useSellingMultiSort from './hooks/useSellingMultiSort';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';

import './SellerBrowsePortfolio.styles.scss';
import './SellerBrowsePortfolio.styles.responsive.scss';

type SellerBrowsePortfolioProps = {
  className?: string;
};

const SellerBrowsePortfolio: React.FC<SellerBrowsePortfolioProps> = (props) => {
  const { className } = props;

  const {
    isEnterpriseHead,
    isSuperAdmin,
    hasEnterprise,
    isRWCMember,
    currentUser: {
      _id: currentUserId,
      isGridViewForDeals,
      hideSellerPortfolioColumns,
    },
  } = useContext(CurrentUserContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const defaultTableColumns = useMemo(
    () =>
      isSuperAdmin
        ? BOSS_MODE_TABLE_COLUMNS
        : isEnterpriseHead
        ? ENTERPRISE_TABLE_COLUMNS
        : TABLE_COLUMNS,
    [isEnterpriseHead, isSuperAdmin],
  );

  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [deals, setDeals] = useState<DealRoom[]>([]);
  const [orderedColumns, setOrderedColumns] = useState(defaultTableColumns);

  const [manageListDrawer, manageListDrawerRef] =
    useCallbackRef<SideDrawerRef>(null);
  const [multiSort, multiSortRef] = useCallbackRef<ModalRef>();

  const classes = classNames('seller-browse-portfolio', className);

  const { limit } = useNumberOfResultsPerPage();

  const isTableView = useMemo(() => !isGridViewForDeals, [isGridViewForDeals]);
  const tableColumns = useMemo(
    () =>
      orderedColumns.filter(
        (col) =>
          !hideSellerPortfolioColumns.some((key) =>
            key.split(',').includes(col),
          ),
      ),
    [hideSellerPortfolioColumns, orderedColumns],
  );

  const headingTranslations = useMemo(
    () => (isRWCMember ? LCW_HEADING_TRANSLATIONS : HEADING_TRANSLATIONS),
    [isRWCMember],
  );

  const displayDealNotification = useActiveBanner(deals);

  const { multiSortValues, multiSortDirectionTranslation } =
    useSellingMultiSort(tableColumns);

  const isEmpty = useMemo(() => !deals.length, [deals.length]);

  const {
    params: { sort },
    setQueryParam,
  } = useQueryParams<
    PortfolioQueryParams & { portfolioFilter?: PortfolioFilter }
  >();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const searchParams = useSearchParams();

  const manageTableOptions = useMemo(
    () =>
      defaultTableColumns.map((el) => ({
        value: el,
        enabled: !REQUIRED_COLUMNS.includes(el),
        label: headingTranslations[el] || t(el),
      })),
    [defaultTableColumns, headingTranslations, t],
  );

  const downloadCSV = useCallback(async () => {
    try {
      setIsDownloading(true);

      const {
        data: { url },
      } = await api.portfolio.admin.getDealsCsv();

      if (url) {
        utils.downloadUrl({
          href: url,
          ...NEW_WINDOW_PROPS,
        });
      }
    } catch (e) {
      popServerError(e);
    } finally {
      setIsDownloading(false);
    }
  }, [popServerError]);

  const updateDeal = useCallback(
    (dealId: string, values: Partial<DealRoom>) =>
      setDeals((old) =>
        old.map((deal) =>
          deal._id === dealId ? { ...deal, ...values } : deal,
        ),
      ),
    [],
  );

  const updateGallery = useCallback(
    (dealId: string, gallery: DropzoneFile[]) =>
      setDeals((old) =>
        old.map((deal) =>
          deal._id === dealId
            ? { ...deal, listing: { ...deal.listing, gallery } }
            : deal,
        ),
      ),
    [],
  );

  const renderDeals = useMemo(
    () =>
      isLoading ? (
        <PortfolioPlaceholderCards numberOfCards={PF_GRID_VIEW_LIMIT} />
      ) : (
        deals.map(
          ({
            _id,
            stage,
            listingStage,
            publishedOn,
            industry,
            dealOutcome,
            otherDealOutcome,
            portfolioName,
            isColleague,
            userId,
            user,
            priceGuide,
            amount,
            amountFrom,
            amountTo,
            franchisorFeePercentage,
            numberOfFiles,
            numberOfLeads,
            isRWC,
            listingEnquiry,
            leadStagesCopies,
            listing,
            listing: { type, expirationDate, seekingExpressionOfInterestDate },
          }) => {
            const {
              _id: enquiryId,
              numberOfAcceptedLeads,
              numberOfConfirmedLeads,
              numberOfEnquiredLeads,
              numberOfOfferedLeads,
              progressedStep,
            } = listingEnquiry || {};

            return (
              <ListingPortfolioCard
                key={_id}
                title={portfolioName}
                dealStage={stage}
                listingStage={listingStage}
                date={publishedOn}
                isColleague={isColleague}
                dealOutcome={dealOutcome}
                otherDealOutcome={otherDealOutcome}
                dealRoomId={_id}
                listingType={type}
                industry={industry}
                seekingExpressionOfInterestDate={
                  seekingExpressionOfInterestDate
                }
                expirationDate={expirationDate}
                ownerId={userId}
                owner={user}
                enquiryId={enquiryId}
                numberOfAcceptedLeads={numberOfAcceptedLeads}
                numberOfConfirmedLeads={numberOfConfirmedLeads}
                numberOfEnquiredLeads={numberOfEnquiredLeads}
                numberOfOfferedLeads={numberOfOfferedLeads}
                progressedStep={progressedStep}
                leadId={null}
                listing={listing}
                priceGuide={priceGuide}
                amount={amount}
                amountFrom={amountFrom}
                amountTo={amountTo}
                franchisorFeePercentage={franchisorFeePercentage}
                numberOfLeads={numberOfLeads}
                numberOfFiles={numberOfFiles}
                isRWC={isRWC}
                allowListingImageUpload={
                  isSuperAdmin ||
                  getIsListingOwner(
                    currentUserId,
                    listing?.userId,
                    listing?.isCoOwner,
                  )
                }
                updateGallery={updateGallery}
                leadStagesCopies={leadStagesCopies}
              />
            );
          },
        )
      ),
    [currentUserId, deals, isLoading, isSuperAdmin, updateGallery],
  );

  const tableContent = useMemo(
    () =>
      orderedColumns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (deal: DealRoom) =>
            BODY_CONTENT_GENERATE[col]({
              deal,
              showBannerNotification: displayDealNotification(deal),
              updateDeal,
              updateGallery,
            }),
        }),
        {},
      ),
    [orderedColumns, displayDealNotification, updateDeal, updateGallery],
  );

  const onRowClick = useCallback(({ _id }) => navigate(_id), [navigate]);

  useEffect(() => {
    const page = +searchParams?.$page;

    if ((totalPages && totalPages < page) || page < 1) {
      setQueryParam('page', FIRST_PAGE);
    }
  }, [searchParams, setQueryParam, totalPages]);

  useEffect(() => {
    if (!sort) {
      setQueryParam('sort', SELLING_DEFAULT_SORT);

      if (isEnterpriseHead || isSuperAdmin)
        setQueryParam(
          'portfolioFilter',
          isEnterpriseHead ? PORTFOLIO_FILTER.MY_DEALS : PORTFOLIO_FILTER.ALL,
        );
    }
  }, [sort, setQueryParam, isEnterpriseHead, isSuperAdmin]);

  useEffect(() => {
    if (!sort) return;

    (async () => {
      try {
        setIsLoading(true);

        const { to, from, userId, colleagueIds, ...restParams } = searchParams;

        const {
          data: { totalPages, items },
        } = await api.dealRoom.getDealsSeller({
          $limit: `${isTableView ? limit : PF_GRID_VIEW_LIMIT}`,
          $relations: '["listing","listingEnquiry","user"]',
          priceTo: to,
          priceFrom: from,
          ...(userId && colleagueIds
            ? {
                $or: `[{"userId": "${userId}"}, {"colleagueIds": "${colleagueIds}"}]`,
              }
            : {}),
          ...restParams,
        });

        setTotalPages(totalPages);
        setDeals(items);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isTableView, searchParams, sort, limit, popServerError]);

  return (
    <div className={classes}>
      <div className="browse-portfolio-heading">
        <div className="browse-portfolio-heading__left">
          <PortfolioHeading />

          {!isRWCMember && <PortfolioSellerOptions />}
        </div>

        <div className="browse-portfolio-heading__right">
          <PortfolioSellerFilters />

          <Button variant="icon" onClick={multiSort?.open}>
            <ArrowupIcon />
            <ArrowdownIcon />
          </Button>
          <MultiSortModal
            ref={multiSortRef}
            options={multiSortValues}
            sortDirectionTranslations={multiSortDirectionTranslation}
          />

          {isSuperAdmin && (
            <Button disabled={isDownloading} onClick={downloadCSV}>
              {t('downloadListingData')}
            </Button>
          )}

          <ViewSelect
            updateProp="isGridViewForDeals"
            isTableView={isTableView}
          />

          <ManageListViewDrawer
            ref={manageListDrawerRef}
            initialHideValues={hideSellerPortfolioColumns}
            updateProp={HIDE_TABLE_COLUMNS.SELLER}
            options={manageTableOptions}
          />
        </div>
      </div>

      <div className="resolve-page-items">
        {!isEmpty || isLoading ? (
          !isTableView ? (
            renderDeals
          ) : (
            <>
              <EditColumnsVisibilityButton
                onClick={() => manageListDrawer?.setOpen(true)}
              />
              <ReorderResizeTable
                className="portfolio-results-table"
                headingTranslations={headingTranslations}
                tableContentTransformations={tableContent}
                columns={tableColumns}
                sortingValues={SORTING_VALUES}
                isDataLoading={isLoading}
                customSizes={CUSTOM_COL_SIZES}
                data={deals as unknown as PortfolioSellerTable[]}
                onRowClick={onRowClick}
                onReorder={setOrderedColumns}
              />
            </>
          )
        ) : hasEnterprise ? (
          <EnterpriseSellerEmptyState />
        ) : (
          <PortfolioDealsEmptyState />
        )}
      </div>

      <ResolvePagination totalPages={totalPages} />
    </div>
  );
};

export default SellerBrowsePortfolio;
